import React, { useContext, useEffect, useState } from "react";
import { Button, Row } from "react-bootstrap";
import Allocation, {
  ParamPart,
  Params,
  SState,
  SubAlloc
} from "../api/types/channelSignedState";
import SignedState from "../api/SignedState";
import { ethers } from "ethers";
import { CKBContext } from "./CKBProvider";
import { AppContext } from "../AppContext";
import {
  dispatchPerunNotice,
  Instructions,
  recoveredFundsNotice
} from "../events/Notice";
import config from "../config.json";
import {
  getFreighterNetwork,
  onboardFr,
  onboardMM,
  switchNetwork
} from "../web3/web3";
import { dispatchPerunError } from "../events/Error";
import Provider, {
  CkbCCCProvider,
  FreighterProvider, Web3Provider
} from "../client/Provider";
import { useTranslation } from "react-i18next";
import { ClientJsonRpc } from "@ckb-ccc/core";
import { newCKBClient } from "../constants/CKBNetwork";
import { Connection } from "../wire/connection";
import { Client } from "../client/Client";
import Success from "../api/Success";

export type FinishSwapProps = {
  initial?: string;
  onSubmit?: (payload: unknown, raw: string) => void;
};

export default function FinishSwap({ initial = "", onSubmit }: FinishSwapProps) {
  const { t } = useTranslation();
  const [text, setText] = useState<string>(initial);
  const ctx = useContext(AppContext);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mmProvider, setMmProvider] =
    useState<ethers.providers.Web3Provider | null>(null);
  const [mmAccount, setMmAccount] = useState<string | null>(null);
  const [stellarAddress, setStellarAddress] = useState("");
  const [signedState, setSignedState] = useState<SignedState | null>(null);
  // The session address this browser remembers for its last swap — the one a
  // user most likely needs to recover. Editable, so a pasted address works too.
  // Deliberately not prefilled: the only session this browser remembers is
  // the current one, and that is never the session a stranded swap ran in.
  const [recoverL2, setRecoverL2] = useState<string>("");
  const [recoverStatus, setRecoverStatus] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  // The manual tools (recover by session address, finish from a pasted
  // state) need knowledge only support has; they are shown only when the page
  // is opened with ?support in the URL.
  const supportMode = new URLSearchParams(window.location.search).has("support");
  const isL2Address = (v: string) => /^0x[0-9a-fA-F]{40}$/.test(v.trim());
  const { signer, address, open } = useContext(CKBContext);

  useEffect(() => {
    const hasVisited = localStorage.getItem("hasVisited");

    if (!hasVisited) {
      dispatchPerunNotice(Instructions());
      localStorage.setItem("hasVisited", "true");
    }
  }, []);

  useEffect(() => {
    if (ctx.client && signedState !== null) {
      console.log("Recovering funds with signed state...");
      (async () => {
        try {
          console.log("Recovering funds with signed state...");
          const res = await ctx.client.recoverFunds(signedState);

          if (res instanceof Success) {
            console.log("Funds recovered successfully:", res);
            dispatchPerunNotice(recoveredFundsNotice());
          } else {
            console.error("Error recovering funds:", res);
            dispatchPerunError(<p>{t("errorRecoveringFunds", {error: res})}</p>);
          }
        } catch (err) {
          console.error("recoverFunds failed:", err);
          dispatchPerunError(<p>{t("errorRecoveringFunds", {error: err})}</p>);
        }
      })();
    }
  }, [ctx.client]);

  async function ensureMMOnce() {
    console.log("Ensuring MM...");
    if (mmProvider !== null && mmAccount !== null) {
      console.log("MM already onboarded");
      return { provider: mmProvider, account: mmAccount };
    }
    const networkIDs = config.networkIDs.map((id) => BigInt(id));
    console.log("Network IDs: ", networkIDs);
    const res = await onboardMM(networkIDs);
    console.log("Onboarded MM: ", res);
    setMmProvider(res.provider);
    setMmAccount(res.account);
    return res;
  }

  const OnboardFr = async (): Promise<FreighterProvider> => {
    try {
      const res = await onboardFr("TESTNET");
      setStellarAddress(res.provider.getAddress());
      return res.provider;
    } catch (err) {
      if (err instanceof Error) {
        dispatchPerunError(<p>{err.message}</p>);
      } else if (err === "Not logged in") {
        dispatchPerunError(<p>{t("errorNoFreighter")}</p>);
      } else {
        dispatchPerunError(<p>{t("errorOnboarding")}</p>);
      }
    }
    return Promise.reject("Could not onboard Freighter");
  };

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);
    setError(null);
    setSuccess(null);
  }

  async function initClient(
    signedState: SignedState,
    ethProvider?: Provider,
    steProvider?: FreighterProvider,
    nervosProvider?: CkbCCCProvider,
  ) {
    const conn = new Connection(
      new URL(`${config.wsAddress}:${config.wsPort + config.wsPath}`),
    );
    let pubKey: string | undefined;
    let nervosAddr: string | undefined;
    if (nervosProvider) {
      nervosAddr = await nervosProvider?.getInternalAddress();
      console.log("----------Address:", nervosAddr);
      const key = "nervospubkey_of_" + nervosAddr;
      let nervosPubKey = localStorage.getItem(key);
      if (nervosPubKey === null) {
        const nervosPubKeyBytes = await nervosProvider?.getPublicKey();
        if (nervosPubKeyBytes) {
          localStorage.setItem(
            key,
            btoa(String.fromCharCode(...nervosPubKeyBytes)),
          );
          nervosPubKey = btoa(String.fromCharCode(...nervosPubKeyBytes!));
          pubKey = nervosPubKey;
        }
      } else {
        pubKey = nervosPubKey;
      }
    }
    if (
      ethProvider == undefined &&
      nervosProvider == undefined &&
      steProvider == undefined
    ) {
      dispatchPerunError(<p>{t("NoProviderConnected")}</p>);
      localStorage.removeItem("nervospubkey");
      return;
    }
    try {
      const client = new Client(
        conn,
        ethProvider,
        steProvider,
        nervosProvider,
        mmAccount!,
        stellarAddress,
        nervosAddr,
        pubKey,
      );

      const isSuccess = await client.finishSwap(signedState)
        .then((success) => {
          if (success) {
            console.log("Client is initialized");
            ctx.setClient(client);
            return true;

          } else {
            dispatchPerunError(<p>{t("CouldNotInitialize")}</p>);
            return false;
          }
        })
        .catch(() => {
          dispatchPerunError(<p>{t("WSNotConnected")}</p>);
          return false;
        });
      if (isSuccess) {
        console.log("Client initialized and set in context");
        return;
      }
    } catch (err) {
      console.log("Error creating client: ", err);
      dispatchPerunError(<p>{t("errorCreatingClient")}</p>);
      return;
    }
  }

  function paramsHasBackend(params: Params, backend: number): boolean {
    return params.parts.some(group =>
      group.some(part => part.backend === backend)
    );
  }

  // Builds a client for a user-triggered recovery session and asks the backend
  // to rebuild the identity behind `l2` from its cached key. If a stranded
  // channel is found the usual FundingChannelFound notice appears with its
  // Recover button. The normal swap client is never touched.
  async function initRecoveryClient(
    l2: string,
    ethProvider?: Provider,
    nervosProvider?: CkbCCCProvider,
  ) {
    const conn = new Connection(
      new URL(`${config.wsAddress}:${config.wsPort + config.wsPath}`),
    );
    let pubKey: string | undefined;
    let nervosAddr: string | undefined;
    if (nervosProvider) {
      nervosAddr = await nervosProvider.getInternalAddress();
      const key = "nervospubkey_of_" + nervosAddr;
      let nervosPubKey = localStorage.getItem(key);
      if (nervosPubKey === null) {
        const bytes = await nervosProvider.getPublicKey();
        if (bytes) {
          nervosPubKey = btoa(String.fromCharCode(...bytes));
          localStorage.setItem(key, nervosPubKey);
        }
      }
      pubKey = nervosPubKey ?? undefined;
    }
    try {
      const client = new Client(
        conn,
        ethProvider,
        undefined,
        nervosProvider,
        mmAccount!,
        stellarAddress,
        nervosAddr,
        pubKey,
      );
      const ok = await client.recoverStranded(l2.trim());
      if (ok) {
        ctx.setClient(client);
        setRecoverStatus(t("recoverStrandedStarted"));
      } else {
        setRecoverStatus(t("recoverStrandedFailed", { error: t("CouldNotInitialize") }));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setRecoverStatus(t("recoverStrandedFailed", { error: msg }));
      dispatchPerunError(<p>{t("errorCreatingClient")}</p>);
    }
  }

  // Connects MetaMask and the CKB signer exactly like the Swap page does and
  // opens a normal session. The backend looks through this wallet's earlier
  // sessions on connect and, if one holds an interrupted swap, the page shows
  // the notice with the Recover button. Nothing to type, nothing to know.
  async function checkWallet() {
    setRecoverStatus(null);
    let ethProvider: Provider | undefined;
    let account = mmAccount;
    try {
      if (mmProvider && mmAccount) {
        ethProvider = new Web3Provider(mmProvider, mmAccount);
      } else {
        const { provider: mm, account: acc } = await ensureMMOnce();
        ethProvider = new Web3Provider(mm, acc);
        setMmProvider(mm);
        setMmAccount(acc);
        account = acc;
      }
    } catch (err) {
      dispatchPerunError(<p>{err instanceof Error ? err.message : t("errorOnboarding")}</p>);
      return;
    }
    if (!signer) {
      open(); // opens the CKB wallet dialog; the user presses the button again once connected
      setRecoverStatus(t("recoverCheckConnectCkb"));
      return;
    }
    const nervosProvider = new CkbCCCProvider(signer, newCKBClient(), account!);
    const nervosAddr = await nervosProvider.getInternalAddress();
    const key = "nervospubkey_of_" + nervosAddr;
    let pubKey = localStorage.getItem(key) ?? undefined;
    if (!pubKey) {
      const bytes = await nervosProvider.getPublicKey();
      if (bytes) {
        pubKey = btoa(String.fromCharCode(...bytes));
        localStorage.setItem(key, pubKey);
      }
    }
    const conn = new Connection(
      new URL(`${config.wsAddress}:${config.wsPort + config.wsPath}`),
    );
    setChecking(true);
    try {
      const client = new Client(
        conn,
        ethProvider,
        undefined,
        nervosProvider,
        account!,
        stellarAddress,
        nervosAddr,
        pubKey,
      );
      const ok = await client.initializeConn();
      if (ok) {
        ctx.setClient(client);
        setRecoverStatus(t("recoverCheckDone"));
      } else {
        setRecoverStatus(t("recoverCheckFailed", { error: t("CouldNotInitialize") }));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setRecoverStatus(t("recoverCheckFailed", { error: msg }));
    } finally {
      setChecking(false);
    }
  }

  // Cross-chain recovery always needs both the Ethereum and the CKB side, so
  // onboard both — the same way onboard() does for a signed state with
  // backends 1 and 3 — then start the recovery session.
  async function recoverByL2() {
    if (!isL2Address(recoverL2)) return;
    setRecoverStatus(null);
    let ethProvider: Provider | undefined;
    let nervosProvider: CkbCCCProvider | undefined;
    let account = mmAccount;
    try {
      if (mmProvider && mmAccount) {
        ethProvider = new Web3Provider(mmProvider, mmAccount);
      } else {
        const { provider: mm, account: acc } = await ensureMMOnce();
        ethProvider = new Web3Provider(mm, acc);
        setMmProvider(mm);
        setMmAccount(acc);
        account = acc;
      }
    } catch (err) {
      dispatchPerunError(<p>{err instanceof Error ? err.message : t("errorOnboarding")}</p>);
      return;
    }
    if (!signer) {
      open(); // opens the CCC wallet UI; the user retries once connected
      setRecoverStatus(t("NoProviderConnected"));
      return;
    }
    nervosProvider = new CkbCCCProvider(signer, newCKBClient(), account!);
    await initRecoveryClient(recoverL2, ethProvider, nervosProvider);
  }

  async function onboard(signedState: SignedState) {
    let steProvider: FreighterProvider | undefined;
    let ethProvider: Provider | undefined;
    let nervosProvider: CkbCCCProvider | undefined;
    console.log("Onboarding backends: ", signedState.state.allocation.backends);
    setSignedState(signedState);
    if (signedState.state.allocation.backends.includes(1) || paramsHasBackend(signedState.params, 1)) {
      try {
        if (mmProvider && mmAccount) {
          ethProvider = new Web3Provider(mmProvider, mmAccount);
        } else {
          console.log("Onboarding MM...");
          const { provider: mm, account } = await ensureMMOnce();
          ethProvider = new Web3Provider(mm, account);
          setMmProvider(mm);
          setMmAccount(account);
        }
      } catch (err) {
        if (err instanceof Error) {
          dispatchPerunError(<p>{err.message}</p>);
        } else {
          dispatchPerunError(<p>{t("errorOnboarding")}</p>);
        }
        return;
      }
    }
    if (signedState.state.allocation.backends.includes(2) || paramsHasBackend(signedState.params, 2)) {
      try {
        console.log("Onboarding Freighter...");
        steProvider = await OnboardFr();
      } catch (err) {
        console.error("Error onboarding Freighter:", err);
        return;
      }
    }
    if (signedState.state.allocation.backends.includes(3) || paramsHasBackend(signedState.params, 3)) {
      const client: ClientJsonRpc = newCKBClient();
      if (!signer) {
        open(); // opens CCC wallet UI once
        return;
      }
      if (mmAccount === null) {
        const { provider: mm, account } = await ensureMMOnce();
        setMmProvider(mm);
        setMmAccount(account);
        nervosProvider = new CkbCCCProvider(
          signer,
          client,
          account,
        );
      } else {
        nervosProvider = new CkbCCCProvider(
          signer,
          client,
          mmAccount,
        );
      }
    }
    await initClient(signedState, ethProvider, steProvider, nervosProvider);
  }

  function buildSubAlloc(obj: any): SubAlloc {
    if (!obj) throw new Error("missing SubAlloc");
    // expecting arrays of numbers
    return new SubAlloc(
      Array.isArray(obj.asId) ? obj.asId.map(Number) : [],
      Array.isArray(obj.bals) ? obj.bals.map(Number) : [],
      Array.isArray(obj.indexMap) ? obj.indexMap.map(Number) : []
    );
  }

  function buildAllocation(obj: any): Allocation {
    if (!obj) throw new Error("missing Allocation");
    const assets: string[] = Array.isArray(obj.assets) ? obj.assets.map(String) : [];
    const backends: number[] = Array.isArray(obj.backends) ? obj.backends.map(Number) : [];
    const balances: Array<Array<string>> = Array.isArray(obj.balances)
      ? obj.balances.map((row: any) => Array.isArray(row) ? row.map(String) : [])
      : [];

    const lockedArr: Array<SubAlloc> = Array.isArray(obj.locked)
      ? obj.locked.map((s: any) => buildSubAlloc(s))
      : [];

    return new Allocation(assets, backends, balances, lockedArr);
  }

  function buildParams(obj: any): Params {
    if (!obj) throw new Error("missing Params");

    const id = Array.isArray(obj.id) ? obj.id.map(Number) : [];
    const challengeDuration = Number(obj.challengeDuration);

    const parts: ParamPart[][] = Array.isArray(obj.parts)
      ? obj.parts.map((group: any[]) =>
        Array.isArray(group)
          ? group.map(
            (p) => new ParamPart(Number(p.backend), String(p.addr))
          )
          : []
      )
      : [];

    return new Params(
      id,
      challengeDuration,
      parts,
      String(obj.app),
      String(obj.appType),
      String(obj.nonce),
      Boolean(obj.ledgerChannel),
      Boolean(obj.virtualChannel)
    );
  }

  function buildSState(obj: any): SState {
    if (!obj) throw new Error("missing SState");
    const id = Array.isArray(obj.id) ? obj.id.map(Number) : [];
    const version = Number(obj.version);
    const app = String(obj.app);
    const appType = String(obj.appType);
    const allocation = buildAllocation(obj.allocation);
    const data = String(obj.data);
    const isFinal = Boolean(obj.isFinal);
    return new SState(id, version, app, appType, allocation, data, isFinal);
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    setSuccess(null);

    if (!text || text.trim() === "") {
      setError("Input your saved signed state JSON.");
      return;
    }

    try {
      console.log(text);
      const parsed = JSON.parse(text);
      setSuccess("JSON parsed successfully");
      if (!parsed.params || !parsed.state || !Array.isArray(parsed.sigs)) {
        throw new Error("Invalid SignedState format");
      }
      const params = buildParams(parsed.params);
      const state = buildSState(parsed.state);
      const sigs: Array<string> = parsed.sigs.map((s: any) => String(s));
      const signedState = new SignedState(params, state, sigs);
      onboard(signedState);
      if (onSubmit) onSubmit(signedState, text);
    } catch (err) {
      setError("Invalid JSON — please check the pasted content.");
      console.error("FinishSwap: JSON parse error", err);
    }

    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 3000);
  }

  return (
    <>
    <div className="w-full max-w-3xl mx-auto bg-white dark:bg-slate-900 p-4 rounded-lg shadow">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
        {t("recoverAutoTitle")}
      </label>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">{t("recoverAutoHint")}</p>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{t("recoverAutoSteps")}</p>
      {ctx.client?.initialized && !recoverStatus && (
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{t("recoverCheckAlreadyConnected")}</p>
      )}
      <div className="d-flex justify-content-center">
        <Button
          className={"custom-button"}
          type={"button"}
          variant={"primary"}
          onClick={checkWallet}
          disabled={checking}
        >
          {checking
            ? t("recoverCheckRunning")
            : ctx.client?.initialized
              ? t("recoverCheckAgain")
              : t("recoverCheckButton")}
        </Button>
      </div>
      <div className="text-sm text-center mt-2">
        {recoverStatus && <span>{recoverStatus}</span>}
      </div>
    </div>

    {supportMode && (
      <>
        <div className="w-full max-w-3xl mx-auto bg-white dark:bg-slate-900 p-4 rounded-lg shadow mt-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
            {t("recoverStrandedTitle")}
          </label>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{t("recoverStrandedHint")}</p>
          <label htmlFor="recoverL2" className="block text-xs text-slate-600 dark:text-slate-300 mb-1">
            {t("recoverStrandedL2Label")}
          </label>
          <input
            id="recoverL2"
            value={recoverL2}
            onChange={(e) => { setRecoverL2(e.target.value); setRecoverStatus(null); }}
            placeholder="0x…"
            className="w-full p-2 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          />
          <div className="d-flex justify-content-center mt-3">
            <Button
              className={"custom-button"}
              type={"button"}
              variant={"primary"}
              onClick={recoverByL2}
              disabled={!isL2Address(recoverL2)}
            >
              {t("recoverStrandedButton")}
            </Button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="w-full max-w-3xl mx-auto bg-white dark:bg-slate-900 p-4 rounded-lg shadow mt-4"
        >
          <label
            htmlFor="signedState"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {t("finishSwapPasteTitle")}
          </label>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{t("finishSwapPasteHint")}</p>
          <textarea
            id="signedState"
            value={text}
            onChange={handleChange}
            placeholder="Paste the signed state JSON here..."
            className="scrollabletextbox w-full h-72 p-3 resize-y rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          />
          <div className="d-flex justify-content-center gap-2 mt-3">
            <Button variant={"secondary"} type={"button"} onClick={() => setText("")}>
              {t("clear")}
            </Button>
            <Button className={"custom-button"} type={"submit"} variant={"primary"} onClick={handleSubmit}>
              {t("finishSwap")}
            </Button>
          </div>
          <div className="text-sm">
            {error && <span className="text-red-600">{error}</span>}
            {success && <span className="text-green-600">{success}</span>}
          </div>
        </form>
      </>
    )}
    </>
  );
}
